import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatNumber } from "@/lib/api";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  BarChart3, Loader2, Sparkles, TrendingUp, TrendingDown,
  Minus, History, Calendar,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const MD_COMPONENTS = {
  h2: ({ children }) => (
    <h2 className="text-base font-semibold text-slate-900 mt-8 mb-3 pb-2 border-b border-slate-200 first:mt-0">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-sm font-semibold text-slate-800 mt-5 mb-2">{children}</h3>
  ),
  p: ({ children }) => <p className="text-sm text-slate-700 leading-relaxed mb-3">{children}</p>,
  ul: ({ children }) => <ul className="space-y-1.5 mb-4 ml-5 list-disc marker:text-indigo-400">{children}</ul>,
  ol: ({ children }) => <ol className="space-y-2 mb-4 ml-5 list-decimal marker:text-indigo-600 marker:font-semibold">{children}</ol>,
  li: ({ children }) => <li className="text-sm text-slate-700 leading-relaxed pl-1">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-slate-900">{children}</strong>,
  table: ({ children }) => (
    <div className="overflow-x-auto mb-4 border border-slate-200 rounded-lg">
      <table className="w-full text-xs">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-slate-50">{children}</thead>,
  th: ({ children }) => <th className="text-left px-3 py-2 font-semibold text-slate-600 whitespace-nowrap">{children}</th>,
  td: ({ children }) => <td className="px-3 py-2 text-slate-700 border-t border-slate-100">{children}</td>,
};

const VERDICT_STYLE = {
  creciendo: { label: "Creciendo", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  estable: { label: "Estable", cls: "bg-sky-100 text-sky-700 border-sky-200" },
  en_riesgo: { label: "En riesgo", cls: "bg-rose-100 text-rose-700 border-rose-200" },
  datos_insuficientes: { label: "Datos insuficientes", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

const _iso = (d) => d.toISOString().slice(0, 10);

function rangePreset(key) {
  const now = new Date();
  const endMonth = new Date(now.getFullYear(), now.getMonth(), 0); // last day of previous month
  const end = _iso(endMonth);
  if (key === "1m") {
    return { from: _iso(new Date(endMonth.getFullYear(), endMonth.getMonth(), 1)), to: end };
  }
  if (key === "3m") {
    return { from: _iso(new Date(endMonth.getFullYear(), endMonth.getMonth() - 2, 1)), to: end };
  }
  return { from: _iso(new Date(endMonth.getFullYear(), endMonth.getMonth() - 5, 1)), to: end };
}

const StatDelta = ({ curr, prev, label, suffix = "" }) => {
  if (curr == null) return null;
  const delta = prev ? ((curr - prev) / prev) * 100 : null;
  return (
    <div className="px-4 py-3 bg-white border border-slate-200 rounded-lg" data-testid={`stat-${label}`}>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-lg font-semibold text-slate-900 tabular-nums">
        {formatNumber(curr)}{suffix}
      </div>
      <div className="flex items-center gap-1 text-xs mt-0.5">
        {delta == null ? (
          <span className="text-slate-400">sin período anterior</span>
        ) : Math.abs(delta) < 1 ? (
          <><Minus className="w-3 h-3 text-slate-400" /><span className="text-slate-500">estable vs período anterior</span></>
        ) : delta > 0 ? (
          <><TrendingUp className="w-3 h-3 text-emerald-600" /><span className="text-emerald-600 font-medium">+{delta.toFixed(0)}% vs anterior</span></>
        ) : (
          <><TrendingDown className="w-3 h-3 text-rose-600" /><span className="text-rose-600 font-medium">{delta.toFixed(0)}% vs anterior</span></>
        )}
      </div>
    </div>
  );
};

export default function AiAnalyticsPage() {
  const qc = useQueryClient();
  const [clientId, setClientId] = useState("");
  const [preset, setPreset] = useState("3m");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [jobId, setJobId] = useState(null);
  const [activeReport, setActiveReport] = useState(null);

  const clientsQ = useQuery({
    queryKey: ["ai-analytics-clients"],
    queryFn: () => api.get("/admin/ai/analytics/eligible-clients").then((r) => r.data),
  });
  const clients = clientsQ.data || [];

  const reportsQ = useQuery({
    queryKey: ["ai-analytics-reports", clientId],
    queryFn: () => api.get("/admin/ai/analytics/reports", { params: { client_id: clientId } }).then((r) => r.data),
    enabled: !!clientId,
  });
  const history = reportsQ.data || [];

  const jobQ = useQuery({
    queryKey: ["ai-analytics-job", jobId],
    queryFn: () => api.get(`/admin/ai/analytics/jobs/${jobId}`).then((r) => r.data),
    enabled: !!jobId,
    refetchInterval: (q) => (q.state.data?.status === "running" ? 3000 : false),
  });

  React.useEffect(() => {
    const j = jobQ.data;
    if (!j || !jobId) return;
    if (j.status === "done" && j.report_id) {
      api.get(`/admin/ai/analytics/reports/${j.report_id}`).then((r) => {
        setActiveReport(r.data);
        setJobId(null);
        qc.invalidateQueries({ queryKey: ["ai-analytics-reports", clientId] });
        toast.success("Análisis listo");
      });
    } else if (j.status === "error") {
      toast.error(`Falló el análisis: ${j.error || "error desconocido"}`);
      setJobId(null);
    }
  }, [jobQ.data, jobId, clientId, qc]);

  const running = !!jobId;

  const startAnalysis = async () => {
    if (!clientId) return toast.error("Elegí un cliente primero");
    let from, to;
    if (preset === "custom") {
      if (!customFrom || !customTo) return toast.error("Completá el rango personalizado");
      from = customFrom; to = customTo;
    } else {
      ({ from, to } = rangePreset(preset));
    }
    try {
      const { data } = await api.post("/admin/ai/analytics/analyze", {
        client_id: clientId, date_from: from, date_to: to,
      });
      setJobId(data.job_id);
      toast.info("Analizando GA4 + Search Console con Claude…");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo iniciar el análisis");
    }
  };

  const openReport = (id) => {
    api.get(`/admin/ai/analytics/reports/${id}`).then((r) => setActiveReport(r.data));
  };

  const rep = activeReport?.report;
  const stats = activeReport?.payload_stats || {};
  const verdict = rep?.veredicto ? VERDICT_STYLE[rep.veredicto] || VERDICT_STYLE.datos_insuficientes : null;

  const rangeLabel = useMemo(() => {
    if (!activeReport) return "";
    return `${activeReport.date_from} → ${activeReport.date_to}`;
  }, [activeReport]);

  return (
    <div className="space-y-6" data-testid="ai-analytics-page">
      {/* Header */}
      <div className="flex items-start gap-4 pb-2 border-b border-slate-200">
        <div className="h-12 w-12 rounded-md bg-gradient-to-br from-indigo-600 to-purple-600 text-white flex items-center justify-center shrink-0">
          <BarChart3 className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.2em] text-indigo-700 font-semibold">Analista IA</div>
          <h1 className="font-display text-2xl font-semibold text-slate-900 tracking-tight">Analytics + Search Console</h1>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">
            Claude analiza la performance real de GA4 y GSC de cualquier cliente, por mes o rango de meses: tendencias, alertas, quick-wins de SEO y oportunidades.
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3 p-4 bg-white border border-slate-200 rounded-lg">
        <div className="space-y-1">
          <label className="text-xs font-medium text-slate-600">Cliente</label>
          <Select value={clientId} onValueChange={setClientId}>
            <SelectTrigger className="w-[220px]" data-testid="analytics-client-select">
              <SelectValue placeholder="Elegir cliente…" />
            </SelectTrigger>
            <SelectContent>
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name} {c.ga4 && c.gsc ? "" : c.ga4 ? "(solo GA4)" : "(solo GSC)"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium text-slate-600">Período</label>
          <Select value={preset} onValueChange={setPreset}>
            <SelectTrigger className="w-[190px]" data-testid="analytics-range-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1m">Último mes cerrado</SelectItem>
              <SelectItem value="3m">Últimos 3 meses</SelectItem>
              <SelectItem value="6m">Últimos 6 meses</SelectItem>
              <SelectItem value="custom">Rango personalizado…</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {preset === "custom" && (
          <>
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-600">Desde</label>
              <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                className="h-9 px-3 border border-slate-300 rounded-md text-sm" data-testid="analytics-date-from" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-600">Hasta</label>
              <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                className="h-9 px-3 border border-slate-300 rounded-md text-sm" data-testid="analytics-date-to" />
            </div>
          </>
        )}

        <Button onClick={startAnalysis} disabled={running || !clientId} data-testid="analytics-run-btn"
          className="bg-indigo-600 hover:bg-indigo-700 text-white">
          {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analizando…</> : <><Sparkles className="w-4 h-4 mr-2" /> Analizar con IA</>}
        </Button>
      </div>

      {running && (
        <div className="flex items-center gap-3 p-4 bg-indigo-50 border border-indigo-200 rounded-lg text-indigo-800 text-sm" data-testid="analytics-running-banner">
          <Loader2 className="w-4 h-4 animate-spin" />
          {jobQ.data?.stage === "analyzing"
            ? "Claude está analizando los datos… (puede tardar ~30 segundos)"
            : "Descargando métricas de GA4 y Search Console…"}
        </div>
      )}

      {/* Report */}
      {rep && (
        <div className="space-y-5" data-testid="analytics-report">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-lg font-semibold text-slate-900">{activeReport.client_name}</h2>
            <span className="text-xs text-slate-500 flex items-center gap-1"><Calendar className="w-3 h-3" /> {rangeLabel}</span>
            {verdict && (
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${verdict.cls}`} data-testid="analytics-verdict">
                {verdict.label}
              </span>
            )}
          </div>

          {/* Mini stats con delta vs período anterior */}
          {(stats.ga4_actual || stats.gsc_actual) && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatDelta label="Sesiones GA4" curr={stats.ga4_actual?.sesiones} prev={stats.ga4_anterior?.sesiones} />
              <StatDelta label="Usuarios GA4" curr={stats.ga4_actual?.usuarios} prev={stats.ga4_anterior?.usuarios} />
              <StatDelta label="Clicks GSC" curr={stats.gsc_actual?.clicks} prev={stats.gsc_anterior?.clicks} />
              <StatDelta label="Impresiones GSC" curr={stats.gsc_actual?.impresiones} prev={stats.gsc_anterior?.impresiones} />
            </div>
          )}

          {/* Análisis narrativo */}
          <div className="p-6 md:p-8 bg-white border border-slate-200 rounded-lg max-w-4xl" data-testid="analytics-narrative">
            {rep.markdown ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{rep.markdown}</ReactMarkdown>
            ) : (
              <p className="text-sm text-slate-700 leading-relaxed" data-testid="analytics-summary">
                {rep.resumen_ejecutivo || rep.resumen || "Sin contenido"}
              </p>
            )}
          </div>
        </div>
      )}

      {/* History */}
      {clientId && history.length > 0 && (
        <div className="space-y-2" data-testid="analytics-history">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-1.5">
            <History className="w-3.5 h-3.5" /> Análisis anteriores
          </div>
          <div className="divide-y divide-slate-100 bg-white border border-slate-200 rounded-lg">
            {history.map((h) => (
              <button key={h.id} onClick={() => openReport(h.id)}
                data-testid={`analytics-history-${h.id}`}
                className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors">
                <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
                  {h.date_from} → {h.date_to}
                  {h.report?.veredicto && (
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${(VERDICT_STYLE[h.report.veredicto] || VERDICT_STYLE.datos_insuficientes).cls}`}>
                      {(VERDICT_STYLE[h.report.veredicto] || VERDICT_STYLE.datos_insuficientes).label}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{h.report?.resumen || h.report?.resumen_ejecutivo}</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
